import React from 'react';
import { motion } from 'framer-motion';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HomeIcon from '@mui/icons-material/Home';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Address } from '../../models/Address';

interface AddressCardProps {
  address: Address;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
}

const AddressCard: React.FC<AddressCardProps> = ({
  address,
  onEdit,
  onDelete,
  onSetDefault,
}) => {
  // Upewnij się, że wszystkie wymagane pola adresu istnieją
  if (!address || typeof address !== 'object') {
    console.error('Nieprawidłowy obiekt adresu:', address);
    return null;
  }

  try {
    // Bezpieczne formatowanie linii adresu z walidacją
    const street = address.street || '';
    const buildingNumber = address.buildingNumber || '';
    const apartmentNumber = address.apartmentNumber || '';

    const addressLine1 = [
      street,
      `${buildingNumber}${apartmentNumber ? `/${apartmentNumber}` : ''}`,
    ]
      .join(' ')
      .trim();

    const postalCode = address.postalCode || '';
    const city = address.city || '';
    const province = address.province || '';

    const addressLine2 =
      `${postalCode} ${city}${province ? `, ${province}` : ''}`.trim();

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        transition={{ duration: 0.3 }}
        className={`bg-white rounded-lg border ${
          address.isDefault ? 'border-blue-200' : 'border-gray-200'
        } overflow-hidden hover:shadow-md transition-shadow`}
      >
        <div className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center">
                <h3 className="font-medium text-gray-900 text-base">
                  {address.name || 'Adres bez nazwy'}
                </h3>
                {address.isDefault && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                    <FavoriteIcon fontSize="inherit" className="mr-1" />
                    Domyślny
                  </span>
                )}
              </div>
            </div>

            <div className="flex space-x-1">
              <motion.button
                onClick={onEdit}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full focus:outline-none"
                title="Edytuj adres"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="button"
              >
                <EditIcon fontSize="small" />
              </motion.button>
              <motion.button
                onClick={onDelete}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full focus:outline-none"
                title="Usuń adres"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                type="button"
              >
                <DeleteIcon fontSize="small" />
              </motion.button>
              {!address.isDefault && (
                <motion.button
                  onClick={onSetDefault}
                  className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full focus:outline-none"
                  title="Ustaw jako domyślny"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  type="button"
                >
                  <FavoriteBorderIcon fontSize="small" />
                </motion.button>
              )}
            </div>
          </div>

          <div className="flex flex-col space-y-2 text-gray-600 mb-2">
            <div className="flex items-center">
              <LocationOnIcon
                fontSize="small"
                className="mr-2 text-gray-500 flex-shrink-0"
              />
              <div>
                <p>{addressLine1 || 'Brak adresu'}</p>
                <p>{addressLine2}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
            {address.phone ? (
              <div className="text-sm text-gray-500 flex items-center">
                <LocalPhoneIcon
                  fontSize="small"
                  className="mr-1 flex-shrink-0"
                />
                <span className="truncate">{address.phone}</span>
              </div>
            ) : (
              <div className="text-sm text-gray-400 flex items-center">
                <HomeIcon fontSize="small" className="mr-1 flex-shrink-0" />
                <span>Adres dostawy</span>
              </div>
            )}

            {!address.isDefault && (
              <motion.button
                onClick={onSetDefault}
                className="flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors text-sm focus:outline-none"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                type="button"
              >
                Ustaw jako domyślny
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    );
  } catch (error) {
    console.error('Błąd podczas renderowania AddressCard:', error);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">
          Wystąpił błąd podczas renderowania adresu.
        </p>
      </div>
    );
  }
};

export default AddressCard;
